import { useState, useMemo } from 'react';
import { SelectBox } from 'devextreme-react/select-box';
import { TextBox } from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button';

const CREATE_LINK_STYLE = {
  background: 'none',
  border: 'none',
  color: '#337ab7',
  cursor: 'pointer',
  fontSize: '12px',
  padding: '4px 0',
  display: 'block',
};

const MINI_FORM_STYLE = {
  border: '1px solid #ddd',
  borderRadius: '4px',
  padding: '8px',
  marginTop: '4px',
  backgroundColor: '#fafafa',
};

const FIELD_LABEL_STYLE = { display: 'block', fontSize: '12px', marginBottom: '2px', color: '#555' };

export function CompanySelectorWithCreate({
  apiService,
  value,
  onValueChanged,
  placeholder = 'Select a company...',
}) {
  const [dsKey, setDsKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyTypeId, setCompanyTypeId] = useState(null);
  const [saving, setSaving] = useState(false);

  // dsKey is bumped after a create so this rebuilds the store and the new company appears.
  // ESLint reports it as unnecessary because it is not read in the body - it is the cache key.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataSource = useMemo(() => apiService.getCompaniesDataSource(), [apiService, dsKey]);
  const companyTypesDs = useMemo(() => apiService.getCompanyTypesDataSource(), [apiService]);

  function handleCancel() {
    setCreating(false);
    setCompanyName('');
    setCompanyTypeId(null);
  }

  async function handleCreate() {
    if (!companyName || !companyTypeId) return;
    setSaving(true);
    try {
      const created = await apiService.createCompany({ displayName: companyName, companyTypeId });
      setDsKey(k => k + 1);
      handleCancel();
      onValueChanged?.({ selectedItem: { id: created.id, displayName: created.displayName } });
    } catch (err) {
      console.error('Failed to create company', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <SelectBox
        dataSource={dataSource}
        displayExpr="displayName"
        valueExpr="id"
        value={value}
        searchEnabled={true}
        placeholder={placeholder}
        onSelectionChanged={(e) => onValueChanged?.(e)}
      />
      {!creating ? (
        <button style={CREATE_LINK_STYLE} onClick={() => setCreating(true)}>
          + Create New Company
        </button>
      ) : (
        <div style={MINI_FORM_STYLE}>
          <div style={{ marginBottom: '6px' }}>
            <label style={FIELD_LABEL_STYLE}>Company Name *</label>
            <TextBox
              value={companyName}
              onValueChanged={(e) => setCompanyName(e.value)}
              placeholder="Enter company name"
              valueChangeEvent="input"
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={FIELD_LABEL_STYLE}>Type *</label>
            <SelectBox
              dataSource={companyTypesDs}
              displayExpr="name"
              valueExpr="id"
              value={companyTypeId}
              onValueChanged={(e) => setCompanyTypeId(e.value)}
              placeholder="Select type..."
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <Button text="Cancel" stylingMode="text" onClick={handleCancel} disabled={saving} />
            <Button
              text="Create"
              type="default"
              stylingMode="outlined"
              onClick={handleCreate}
              disabled={!companyName || !companyTypeId || saving}
            />
          </div>
        </div>
      )}
    </div>
  );
}
